// AppExperienceBlock — showcases geoVINUM app (the app itself) as the central
// pillar of the experience. Highlights the 5 in-app usages (Explorer, Taste,
// Learn, AI, Experience) with mini-thumbnails from real app screenshots.
// i18n : dictionnaire inline COPY (fr/en/es/pt).
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, Compass, Wine, GraduationCap, Sparkles, Gamepad2 } from "lucide-react";
import { useLang } from "@/lib/i18n";

const APP_CDN = "https://customer-assets-7cd3h4nn.emergentagent.net/job_geo-vinum-preview/artifacts";

const IMAGES = {
  explore: `${APP_CDN}/rydikzrt_geovinum-app-explorer.png`,
  taste: `${APP_CDN}/clrkro80_geovinum-app-degustation.png`,
  learn: `${APP_CDN}/ztj44f7r_geovinum-app-microlearning.png`,
  ai: `${APP_CDN}/urmu0z3s_geovinum-app-plusieurs-fac%CC%A7ons-dapprendre.png`,
  vision: `${APP_CDN}/d8m4qo14_geovinum-app-vision.png`,
};

const ICONS = { explore: Compass, taste: Wine, learn: GraduationCap, ai: Sparkles, vision: Gamepad2 };

const COPY = {
  fr: {
    eyebrow: "geoVINUM app · l'application",
    h2_a: "L'IA qui vous accompagne", h2_b: "avant, pendant et après", h2_c: "la dégustation.",
    body: "geoVINUM app n'est pas seulement un chatbot. C'est l'expérience applicative complète : l'IA est connectée à l'Explorer, au carnet de dégustation, à l'Academy et aux expériences interactives.",
    open_app: "Ouvrir l'app", learn_more: "En savoir plus",
    usages: {
      explore: { title: "Explorer", desc: "Appellations · cépages · producteurs · régions." },
      taste: { title: "Déguster", desc: "Carnet de dégustation, profil gustatif, recommandations." },
      learn: { title: "Apprendre en arrière-plan", desc: "Microlearning, flashcards SM-2, sujets favoris — quelques minutes par jour." },
      ai: { title: "Converser avec l'IA", desc: "Coach personnel, questions, itinéraires, explications." },
      vision: { title: "Photographier", desc: "Une bouteille, une étiquette, un paysage — geoVINUM app raconte." },
    },
  },
  en: {
    eyebrow: "geoVINUM app · the application",
    h2_a: "The AI that guides you", h2_b: "before, during and after", h2_c: "the tasting.",
    body: "geoVINUM app is not just a chatbot. It's the full app experience: the AI is connected to the Explorer, tasting journal, Academy and interactive experiences.",
    open_app: "Open the app", learn_more: "Learn more",
    usages: {
      explore: { title: "Explore", desc: "Appellations · grapes · producers · regions." },
      taste: { title: "Taste", desc: "Tasting journal, taste profile, recommendations." },
      learn: { title: "Learn in the background", desc: "Micro-learning, SM-2 flashcards, favourite topics — a few minutes a day." },
      ai: { title: "Chat with the AI", desc: "Personal coach, questions, itineraries, explanations." },
      vision: { title: "Snap a photo", desc: "A bottle, a label, a landscape — geoVINUM app tells the story." },
    },
  },
  es: {
    eyebrow: "geoVINUM app · la aplicación",
    h2_a: "La IA que te acompaña", h2_b: "antes, durante y después", h2_c: "de la cata.",
    body: "geoVINUM app no es solo un chatbot. Es la experiencia aplicativa completa: la IA está conectada al Explorer, al cuaderno de cata, a la Academy y a las experiencias interactivas.",
    open_app: "Abrir la app", learn_more: "Saber más",
    usages: {
      explore: { title: "Explorar", desc: "Denominaciones · variedades · productores · regiones." },
      taste: { title: "Catar", desc: "Cuaderno de cata, perfil gustativo, recomendaciones." },
      learn: { title: "Aprender en segundo plano", desc: "Microaprendizaje, flashcards SM-2, temas favoritos — unos minutos al día." },
      ai: { title: "Conversar con la IA", desc: "Coach personal, preguntas, itinerarios, explicaciones." },
      vision: { title: "Fotografiar", desc: "Una botella, una etiqueta, un paisaje — geoVINUM app lo cuenta." },
    },
  },
  pt: {
    eyebrow: "geoVINUM app · a aplicação",
    h2_a: "A IA que o acompanha", h2_b: "antes, durante e depois", h2_c: "da prova.",
    body: "A geoVINUM app não é apenas um chatbot. É a experiência aplicativa completa: a IA está ligada ao Explorer, ao caderno de prova, à Academy e às experiências interativas.",
    open_app: "Abrir a app", learn_more: "Saber mais",
    usages: {
      explore: { title: "Explorar", desc: "Denominações · castas · produtores · regiões." },
      taste: { title: "Provar", desc: "Caderno de prova, perfil gustativo, recomendações." },
      learn: { title: "Aprender em segundo plano", desc: "Microaprendizagem, flashcards SM-2, tópicos favoritos — alguns minutos por dia." },
      ai: { title: "Conversar com a IA", desc: "Coach pessoal, perguntas, itinerários, explicações." },
      vision: { title: "Fotografar", desc: "Uma garrafa, um rótulo, uma paisagem — a geoVINUM app conta." },
    },
  },
};

const USAGE_KEYS = ["explore", "taste", "learn", "ai", "vision"];

const AppExperienceBlock = () => {
  const lang = useLang();
  const c = COPY[lang] || COPY.fr;
  return (
    <section className="py-24 md:py-32 bg-[color:var(--gv-ink)] text-white" data-testid="home-app-experience">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start mb-12">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-4">
              <Sparkles size={13} /> {c.eyebrow}
            </div>
            <h2 className="font-display text-[36px] md:text-[52px] leading-[1.05] text-white">
              {c.h2_a} <span className="italic text-[color:var(--gv-pink)]">{c.h2_b}</span> {c.h2_c}
            </h2>
          </div>
          <div className="lg:col-span-6 lg:pt-6">
            <p className="text-[16px] text-white/70 max-w-lg mb-6">
              {c.body}
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="https://app.geovinum.io"
                target="_blank"
                rel="noreferrer"
                className="btn-primary"
                data-testid="app-experience-open"
              >
                {c.open_app} <ExternalLink size={14} />
              </a>
              <Link to="/geovinum-ai" className="text-white/85 inline-flex items-center gap-2 text-[14px] hover:text-[color:var(--gv-pink)] px-4 py-2.5" data-testid="app-experience-details">
                {c.learn_more} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>

        {/* 5-usage grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {USAGE_KEYS.map((key) => {
            const Icon = ICONS[key];
            const u = c.usages[key];
            return (
              <div
                key={key}
                className="bg-white/[0.04] backdrop-blur border border-white/10 rounded-[6px] overflow-hidden hover:border-[color:var(--gv-pink)]/60 hover:bg-white/[0.06] transition-all reveal"
                data-testid={`app-usage-${key}`}
              >
                <div className="aspect-[4/3] bg-white/[0.03] overflow-hidden">
                  <img src={IMAGES[key]} alt="" loading="lazy" className="w-full h-full object-cover object-top opacity-95" />
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Icon size={15} className="text-[color:var(--gv-pink)]" />
                    <div className="font-display text-[15.5px] text-white">{u.title}</div>
                  </div>
                  <div className="text-[12.5px] text-white/60 leading-snug">{u.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AppExperienceBlock;
